import apiClient from './apiClient';
import { AxiosResponse } from 'axios';

// Interface for SSOT schema
export interface SsotSchema {
  id?: number;
  name: string;
  description: string;
  job_type: number;
  schema_json: any;
  schema_yaml?: string; // YAML representation of the schema
  schema_type?: string;
  is_active?: boolean;
  schema_definition?: any;
  created_at?: string;
  updated_at?: string;
  schema?: {
    name?: string;
    description?: string;
    schema_json?: any;
    schema_yaml?: string;
  };
}

// Interface for SSOT schema generation request
export interface GenerateSsotRequest {
  source_platform_id: number;
  target_platform_id: number;
  job_type: number;
}

// Interface for SSOT schema review and save request
export interface SaveSsotRequest {
  id: number; // Changed from schema_id to id to match API expectation
  schema_json?: any;
  schema_yaml?: string;
  name?: string;
  description?: string;
  job_type?: number; // Added job_type field
}

export interface SaveYamlConfigRequest {
  yaml_config: string;
  config_name: string;
  description: string;
  config_type: number; // 1 for extraction, 2 for loading, etc.
  extractor_id?: number;
  loader_id?: number;
  platform_id?: number;
  job_type_id?: number;
}

export interface PaginatedSsotSchemasResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SsotSchema[];
}

export const ssotService = {
  async generateSsotSchema(request: GenerateSsotRequest): Promise<SsotSchema | null> {
    try {
      // Add timeout to ensure we don't wait forever
      const response = await apiClient.post<any>('/api/ssot-schemas/generate/', request, {
        timeout: 30000 // 30 seconds timeout
      });
      
      
      // Handle different response formats
      let schema: SsotSchema;
      
      if (response.data.schema) {
        // If the API returns a nested schema object
        schema = response.data.schema;
      } else if (response.data.message && response.data.message.includes('generated')) {
        // If the API returns a success message with schema details
        schema = {
          id: response.data.id || 0,
          name: response.data.name || (response.data.schema && response.data.schema.name) || 
                `Schema for ${request.source_platform_id} to ${request.target_platform_id}`,
          description: response.data.description || (response.data.schema && response.data.schema.description) || 
                       'Generated SSOT schema',
          job_type: request.job_type,
          schema_json: response.data.schema_json || (response.data.schema && response.data.schema.schema_json)
        };
      } else {
        // Default case - use the response data directly
        schema = response.data;
      }
      
      // Ensure schema has required properties
      if (!schema.name) {
        schema.name = `Schema for ${request.source_platform_id} to ${request.target_platform_id}`;
      }
      
      if (!schema.description) {
        schema.description = 'Generated SSOT schema';
      }
      
      // Check for schema_yaml in the response
      if (response.data.schema_yaml) {
        schema.schema_yaml = response.data.schema_yaml;
      } else if (response.data.schema && response.data.schema.schema_yaml) {
        schema.schema_yaml = response.data.schema.schema_yaml;
      }
      
      // Ensure schema_json is properly handled
      if (!schema.schema_json) {
        // If we have schema_yaml but no schema_json, use the YAML
        if (schema.schema_yaml) {
          schema.schema_json = schema.schema_yaml;
        } 
        // If schema_json is missing but we have a schema object
        else if (schema.schema && schema.schema.schema_json) {
          schema.schema_json = schema.schema.schema_json;
        } 
        // If schema_json is missing but we have schema_yaml in the schema object
        else if (schema.schema && schema.schema.schema_yaml) {
          schema.schema_json = schema.schema.schema_yaml;
        }
        // Create a minimal default schema as last resort
        else {
          schema.schema_json = {
            name: schema.name,
            description: schema.description,
            properties: {}
          };
        }
      }
      
      // Handle string schema_json
      if (typeof schema.schema_json === 'string') {
        try {
          // Try to parse it if it's a JSON string
          const parsedJson = JSON.parse(schema.schema_json);
          schema.schema_json = parsedJson;
        } catch (e) {
          // If it's not valid JSON, keep it as is but log it
        }
      }
      
      return schema;
    } catch (error) {
      return null;
    }
  },

  async getSsotSchemaById(schemaId: number): Promise<SsotSchema | null> {
    try {
      const response = await apiClient.get<SsotSchema>(`/api/ssot-schemas/${schemaId}/`);
      return response.data;
    } catch (error) {
      return null;
    }
  },

  async reviewAndSaveSsotSchema(request: SaveSsotRequest): Promise<SsotSchema | null> {
    try {
      
      // Prepare the request payload
      const payload: any = {
        id: request.id
      };
      
      // Add optional fields if provided
      if (request.name) payload.name = request.name;
      if (request.description) payload.description = request.description;
      if (request.job_type) payload.job_type = request.job_type;
      
      // Prioritize schema_yaml if available (for YAML content)
      if (request.schema_yaml) {
        payload.schema_yaml = request.schema_yaml;
      }
      // Otherwise use schema_json
      else if (request.schema_json) {
        if (typeof request.schema_json === 'string') {
          // Check if this is actually YAML content in the schema_json field
          if (request.schema_json.includes('\n') && 
              (request.schema_json.includes(':') || request.schema_json.includes('-'))) {
            payload.schema_yaml = request.schema_json;
          } else {
            // If it's a JSON string, send as is
            payload.schema_json = request.schema_json;
          }
        } else {
          // If it's an object, stringify it
          payload.schema_json = JSON.stringify(request.schema_json);
        }
      }
      
      
      // Set timeout to ensure we don't wait forever
      const response = await apiClient.post<any>('/api/ssot-schemas/review-and-save/', payload, {
        timeout: 30000, // 30 seconds timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      
      // Handle the response format from Postman
      if (response.data) {
        // Check if the response has a message indicating success
        if (response.data.message && response.data.message.includes('saved SSOT schema')) {
          
          // Extract the schema from the response
          if (response.data.schema) {
            return response.data.schema;
          } else {
            // If no schema in response but we have a success message, create a minimal schema object
            return {
              id: request.id,
              name: request.name || '',
              description: request.description || '',
              job_type: 0, // Default value
              schema_json: request.schema_json || request.schema_yaml || '{}',
              schema_yaml: request.schema_yaml || ''
            };
          }
        } else {
          // Regular response format
          return response.data;
        }
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  },

  async getAllSsotSchemas(): Promise<SsotSchema[]> {
    const allSchemas: SsotSchema[] = [];
    let url: string | null = '/api/ssot-schemas/';

    try {
      while (url) {
        const response: AxiosResponse<PaginatedSsotSchemasResponse> = await apiClient.get(url);
        const { results, next } = response.data;
        allSchemas.push(...results);
        url = next;
      }

      return allSchemas;
    } catch (error) {
      return [];
    }
  },

  async deleteSsotSchema(schemaId: number): Promise<boolean> {
    try {
      const response = await apiClient.delete(`/api/ssot-schemas/${schemaId}/`);
      return response.status === 200 || response.status === 204;
    } catch (error) {
      return false;
    }
  },

  async saveYamlConfig(request: SaveYamlConfigRequest): Promise<any> {
    try {
      const response = await apiClient.post('/api/ssot/save-yaml-config/', request, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to save YAML config:', error);
      throw error;
    }
  }
};
